import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SideBarAdmin from '../../components/SideBarAdmin';
import { useAxios } from '../../utils/hook/useAxios';

moment.locale('fr');

const STATUS_BADGE = {
  pending: { label: 'En attente', cls: 'bg-warning-subtle text-warning' },
  reviewed: { label: 'Examiné', cls: 'bg-info-subtle text-info' },
  actioned: { label: 'Traité', cls: 'bg-success-subtle text-success' },
  dismissed: { label: 'Rejeté', cls: 'bg-secondary-subtle text-secondary' },
};

const REASON_LABEL = {
  contenu_inapproprie: 'Contenu inapproprié',
  informations_fausses: 'Infos fausses',
  prix_abusif: 'Prix abusif',
  fraude: 'Fraude',
  autre: 'Autre',
};

function AdminReportsPage() {
  const axios = useAxios();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected] = useState(null); // rapport en cours de traitement
  const [adminNote, setAdminNote] = useState('');
  const [newStatus, setNewStatus] = useState('reviewed');
  const [saving, setSaving] = useState(false);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/annonce-reports');
      setReports(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const filtered =
    filterStatus === 'all' ? reports : reports.filter((r) => r.status === filterStatus);

  const countByStatus = (s) =>
    s === 'all' ? reports.length : reports.filter((r) => r.status === s).length;

  const openModal = (report) => {
    setSelected(report);
    setAdminNote(report.adminNote || '');
    setNewStatus(report.status || 'reviewed');
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`/annonce-reports/${selected.id}`, { status: newStatus, adminNote });
      await fetchReports();
      setSelected(null);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-body-tertiary min-vh-100 pb-5">
      <div className="container py-4">
        <div className="row g-4">
          {/* Sidebar */}
          <div className="col-md-3 d-none d-md-block">
            <SideBarAdmin />
          </div>

          {/* Contenu */}
          <div className="col-12 col-md-9">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h1 className="h4 fw-bold mb-0">
                <i className="bi bi-flag-fill text-danger me-2"></i>Annonces signalées
              </h1>
              <span className="badge bg-danger rounded-pill">
                {countByStatus('pending')} en attente
              </span>
            </div>

            {/* Filtres statut */}
            <div className="d-flex gap-2 flex-wrap mb-4">
              {[
                { key: 'all', label: 'Tous' },
                { key: 'pending', label: 'En attente' },
                { key: 'reviewed', label: 'Examinés' },
                { key: 'actioned', label: 'Traités' },
                { key: 'dismissed', label: 'Rejetés' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`btn btn-sm rounded-pill ${filterStatus === key ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => setFilterStatus(key)}
                >
                  {label}
                  <span className="ms-1 badge rounded-pill bg-secondary bg-opacity-25 text-dark">
                    {countByStatus(key)}
                  </span>
                </button>
              ))}
            </div>

            {/* Table */}
            {loading ? (
              <div className="text-center py-5 text-muted">
                <div className="spinner-border spinner-border-sm me-2" />
                Chargement…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-flag-fill d-block mb-2" style={{ fontSize: 36 }}></i>
                Aucun signalement dans cette catégorie.
              </div>
            ) : (
              <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
                <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Annonce</th>
                      <th>Signalé par</th>
                      <th>Raison</th>
                      <th>Date</th>
                      <th>Statut</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => {
                      const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                      return (
                        <tr key={r.id}>
                          <td className="text-muted">{r.id}</td>
                          <td>
                            <Link
                              to={`/admin/annonces/${r.annonceId}`}
                              className="text-decoration-none fw-semibold"
                            >
                              Annonce #{r.annonceId}
                            </Link>
                            {r.VehiculeAnnonce?.locationAddress && (
                              <div className="text-muted small">
                                {r.VehiculeAnnonce.locationAddress}
                              </div>
                            )}
                          </td>
                          <td>
                            {r.Reporter
                              ? `${r.Reporter.firstName} ${r.Reporter.lastName}`
                              : `#${r.reporterId}`}
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border">
                              {REASON_LABEL[r.reason] || r.reason}
                            </span>
                          </td>
                          <td className="text-muted">{moment(r.createdAt).fromNow()}</td>
                          <td>
                            <span className={`badge rounded-pill ${badge.cls}`}>{badge.label}</span>
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => openModal(r)}
                            >
                              Traiter
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal traitement */}
      {selected && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header">
                <h5 className="modal-title fw-semibold">
                  <i className="bi bi-flag-fill text-danger me-2"></i>
                  Traiter le signalement #{selected.id}
                </h5>
                <button className="btn-close" onClick={() => setSelected(null)} />
              </div>
              <div className="modal-body">
                {/* Détails */}
                <div className="mb-3 p-3 rounded-3 bg-body-tertiary" style={{ fontSize: 13 }}>
                  <p className="mb-1">
                    <strong>Annonce :</strong> #{selected.annonceId}
                  </p>
                  <p className="mb-1">
                    <strong>Signalé par :</strong>{' '}
                    {selected.Reporter
                      ? `${selected.Reporter.firstName} ${selected.Reporter.lastName}`
                      : `#${selected.reporterId}`}
                  </p>
                  <p className="mb-1">
                    <strong>Raison :</strong> {REASON_LABEL[selected.reason] || selected.reason}
                  </p>
                  {selected.details && (
                    <p className="mb-0">
                      <strong>Détails :</strong> {selected.details}
                    </p>
                  )}
                </div>

                {/* Statut */}
                <div className="mb-3">
                  <label className="form-label fw-semibold small">Nouveau statut</label>
                  <select
                    className="form-select form-select-sm"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <option value="reviewed">Examiné</option>
                    <option value="actioned">Traité — action prise</option>
                    <option value="dismissed">Rejeté — sans suite</option>
                  </select>
                </div>

                {/* Note admin */}
                <div className="mb-3">
                  <label className="form-label fw-semibold small">Note interne (optionnel)</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    placeholder="Ex : annonce désactivée, utilisateur averti…"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setSelected(null)}>
                  Annuler
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default AdminReportsPage;
